/**
 * Game Parsing Utilities
 * Shared utilities for parsing game data across different features
 * Based on old lobby's parseGameSettings function
 */

import type IGameSettings from '../../types/models/IGameSettings';
import type { ApiGameSettings } from '../../types/api/api.types';
import { isGameNew } from './dateHelpers';
import { buildGameBanners, BANNER_TAG } from '../../features/games/constants/gameBanners';

export interface IGroupGame {
  gameKey: string;
  imageLobby: 'vertical' | 'horizontal' | 'rectangular';
  imageSection: 'vertical' | 'horizontal' | 'rectangular';
  order: number;
}

export interface ParseGameSettingsParams {
  mutatedGame: IGroupGame;
  gameData: ApiGameSettings;
  forTournament?: boolean;
  isTopGame?: boolean;
}

/**
 * Parses a single game key into a full IGameSettings object
 * Based on old lobby's parseGameSettings function
 */
export const parseGameSettings = ({
  mutatedGame,
  gameData,
  forTournament = false,
  isTopGame = false
}: ParseGameSettingsParams): IGameSettings | null => {
  if (!gameData) {
    return null;
  }

  // Process min/max bets from betRanges or betOptions
  const maxPossibleBets = new Map<string, number>();
  const minPossibleBets = new Map<string, number>();
  
  if (gameData?.betRanges) {
    // Regular games with betRanges
    Object.entries(gameData.betRanges).forEach(([currency, range]) => {
      const unitsToDivideBy = 100; // Same as old lobby
      minPossibleBets.set(currency, range.minBet / unitsToDivideBy);
      maxPossibleBets.set(currency, range.maxBet / unitsToDivideBy);
    });
  } else if (gameData?.betOptions) {
    // Multiplayer/instant games with betOptions
    Object.entries(gameData.betOptions).forEach(([currency, options]) => {
      const unitsToDivideBy = 100; // Same as old lobby
      const minPlayerBet = options[0] / unitsToDivideBy;
      const maxPlayerBet = options[options.length - 1] / unitsToDivideBy;
      minPossibleBets.set(currency, minPlayerBet);
      maxPossibleBets.set(currency, maxPlayerBet);
    });
  }

  return {
    name: gameData.name,
    gameKey: gameData.gameKey,
    jackpotName: gameData.jackpotName || null,
    displayJackpotName: gameData.displayJackpotName || null,
    order: mutatedGame.order,
    minDenomination: new Map(
      Object.entries(gameData.denominations).map(([k, v]) => [
        k, 
        Math.min(...(v as number[]))
      ])
    ),
    denominations: new Map(Object.entries(gameData.denominations)),
    banners: {
      lobby: { rows: 2, cols: 1 },
      section: { rows: 2, cols: 1 },
    },
    maxBet: new Map(), // Legacy field
    maxPossibleBets,
    minPossibleBets,
    isInTournament: forTournament ? (gameData.isInTournament || true) : (gameData.isInTournament || false),
    gameBanners: buildGameBanners(
      isTopGame && BANNER_TAG.TOP,
      isGameNew(gameData.activatedOn, gameData.addedToLobbyOn) && BANNER_TAG.NEW,
    ),
    statisticRtp: gameData.statisticRtp ?? null,
    gameFeatures: gameData.features,
    maxAwardingCoef: gameData.maxAwardingCoef ?? null,
    paymode: gameData.paymode,
    lines: gameData.lines,
  };
};
